import {
  Archive,
  ArchiveRestore,
  FlaskConical,
  Info,
  Plus,
  Search,
  Star,
} from "lucide-react";
import { useId, useState, type KeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router";

import type { ProjectListItem } from "@/api/hooks";
import {
  queryKeys,
  usePreviewTargetsQuery,
  useProjectsQuery,
} from "@/api/hooks";
import {
  classifyTenantPickFailure,
  usePutTenantPickMutation,
  useTenantPicksQuery,
  type TenantPickFailure,
  type TenantProjectPick,
} from "@/api/tenantPicks";
import {
  ProjectLifecycleDialog,
  type ProjectLifecycleMode,
} from "@/ui/project/ProjectLifecycleDialog";
import { ProjectSettingsDialog } from "@/ui/project/ProjectSettingsDialog";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { PageHeader } from "@/shell/PageHeader";
import { cn } from "@/lib/cn";
import { labPath } from "@/lib/labUrl";
import { publicationBadgeLabel, readinessLabel } from "@/lib/projectStatus";
import { groupByPreviewTenant } from "@/lib/tenantGroups";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { DataRow } from "@/ui/DataRow";
import { IconButton } from "@/ui/IconButton";
import { Input, Select } from "@/ui/Input";
import { useToast } from "@/ui/Toast";
import { Tooltip } from "@/ui/Tooltip";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { EnvironmentsPanel } from "./EnvironmentsPanel";
import { NewProjectDialog } from "./NewProjectDialog";
import { TenantsPanel } from "./TenantsPanel";

/**
 * ONE column track, at every width (#439).
 *
 * The canonical reference declares `2fr 1fr 1.5fr auto` with a 36px action
 * cell, and keeps it at 390 with the header row visible: no stacked variant, no
 * `md:` breakpoint, and therefore no second copy of a row in the DOM for
 * assistive technology to walk twice. Measured on the reference at 1280 —
 * 495.109 / 247.547 / 371.344 / 36 — and this declaration computes the same
 * three numbers there.
 *
 * The last track is 156px rather than 36px: four 36px icon buttons and three
 * 4px gaps, the pick-for-production control (ADR 0008 addendum), Open Reliv
 * Labs, the lifecycle one and Project settings. Header and rows share the template, so alignment
 * is unchanged.
 *
 * `minmax(0, …)` rather than the bare `2fr`, which is the one deliberate
 * departure. An `fr` track's automatic minimum is its content, so at 390 the
 * reference's own rows disagree with their own header by ~16px and a single
 * unbroken project name would push the grid past the card. Flooring each track
 * at zero keeps every row aligned to the header the page already draws and
 * makes overflow unrepresentable rather than merely unobserved; the ratio, the
 * fixed action column and the shared track are unchanged.
 */
/** The page's two views (#1165): today's list, and one row per tenant. */
const VIEWS = [
  { id: "projects", label: "Projects" },
  { id: "tenants", label: "Tenants" },
  { id: "environments", label: "Environments" },
] as const;
type ViewId = (typeof VIEWS)[number]["id"];

const COLUMNS = "grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.5fr)_156px]";

/*
 * The per-row lifecycle control (#440, over #434's service half), and what
 * gates it: the CALLER's capability alone — `project.archive` for an active
 * row, `project.restore` (admin-alone, the ratified contract's §2.1) for an
 * archived one. Archive stays disabled-with-reason when capability is absent;
 * Restore is different by #440's explicit boundary: ordinary clinicians and
 * project editors must not SEE it, so an archived row has no lifecycle control
 * unless the caller actually holds `project.restore`. There is deliberately
 * no pre-flight eligibility verdict to render: `ProjectArchiveStatus` carries
 * facts, and the one state-refusal the service raises
 * (`publication_in_flight`) is answered by the operation itself with a stable
 * reason, surfaced in the confirmation dialog — #650's capability contract
 * owns folding that verdict into the read models, and deriving one here from
 * readiness or publication state would be exactly the client-only archive
 * flag this ticket names a non-goal.
 *
 * Disabled-with-reason, never absent (`Button`/`IconButton` express it as
 * `aria-disabled` so the element keeps its tab-order place and the reason
 * stays hover- and keyboard-reachable).
 */

const PICK_FAILURE_MESSAGES = {
  stale:
    "Another clinician changed this preview target's pick first. The picks have been reloaded — check them and decide again.",
  project_ineligible:
    "That project cannot be picked: its preview target is a different one, or it is archived.",
  project_unavailable: "That project is no longer available to you.",
  denied: "Your account cannot pick a project.",
  error: "The pick could not be saved. Nothing was changed; try again.",
} as const satisfies Record<TenantPickFailure["kind"], string>;

function ColumnHeader() {
  return (
    <div
      className={`grid ${COLUMNS} items-center gap-3 border-b border-border px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground`}
    >
      {/*
        Four columns, and only these four (#439's canonical convergence
        amendment). `Base pack` is gone with every rendering of
        `baseContentSha256`: a 12-character content digest occupied the space a
        clinician reads for clinical status, which is what the amendment
        replaces it with. The digest itself is untouched on the API.

        The header stays MOUNTED at every width. It used to carry
        `max-md:hidden`, so a narrow screen showed cells with nothing naming
        them; the canonical reference keeps it visible at 390 and so does this.
      */}
      <span>Project</span>
      <span>Status</span>
      <span>Preview target</span>
      {/*
        Unlabelled on the canonical surface, which draws an empty cell here.
        Named for assistive technology only, as `LibraryCatalog` does: a column
        of controls with no header is a column a screen-reader user meets
        without warning.
      */}
      <span className="sr-only">Actions</span>
    </div>
  );
}

function ProjectRow({
  project,
  archiveRefusal,
  restoreAllowed,
  onLifecycle,
  onSettings,
  picked,
  pickRefusal,
  onPick,
}: {
  project: ProjectListItem;
  /** This Project is the one picked for production for its tenant. */
  picked: boolean;
  /** Why this caller cannot pick this Project, or `null` when they can. */
  pickRefusal: string | null;
  onPick: (project: ProjectListItem) => void;
  /** Why this caller cannot archive, or `null` when they can. */
  archiveRefusal: string | null;
  /** Restore is rendered only for the admin/operations capability. */
  restoreAllowed: boolean;
  onLifecycle: (project: ProjectListItem, mode: ProjectLifecycleMode) => void;
  onSettings: (project: ProjectListItem) => void;
}) {
  /*
    BOTH badges are read straight off the projection and neither is composed.
    `readiness` is the rung the authoring has reached and `publicationStatus`
    is #267's compact clinician status — the same value the Project page shows,
    which is why `lib/projectStatus` holds one table for it rather than two.
    The second badge is omitted entirely on `none`; the label module says why.

    What is NOT here is as much of the point as what is: no `attentionReason`,
    no `nextAction`, no `currentRevision`, no `lineage.sourceVersion`, no
    `lastMeaningfulActivityAt`. They all arrive on the wire and this surface
    renders none of them.
  */
  const publicationBadge = publicationBadgeLabel(
    project.publicationStatus.state,
  );

  /*
    The lineage note the canonical surface puts under a copied Project's name,
    and only there. `origin` decides — never the presence of a name — so a
    seeded Project whose `sourceName` is its base pack does not acquire a note
    claiming it was copied from one. The snapshot wording is the contract's own
    reading of the field: the source name is "an IMMUTABLE SNAPSHOT taken when
    the Project was created … deliberately not re-resolved".
  */
  const copiedFrom =
    project.lineage.origin === "copied_project" && project.lineage.sourceName
      ? `Copied from ${project.lineage.sourceName} · starting snapshot`
      : null;

  /*
    The lifecycle control's direction comes from the row's own archive FACTS
    (#434's `archive.archived`), and the refusal from the caller's capability
    for that direction — see the module comment above for why there is no
    per-project eligibility verdict to consult. `archive` is optional in the
    generated type; an absent status reads as active, but its action fails
    closed with a reload reason before the dialog rather than defaulting a CAS
    revision the service never sent.
  */
  const archived = project.archive?.archived === true;
  const mode: ProjectLifecycleMode = archived ? "restore" : "archive";
  const refusal =
    project.archive?.archiveRevision === undefined
      ? "The Editor Service did not report this project's archive revision. Reload before changing its archive state."
      : archived
        ? null
        : archiveRefusal;
  const actionLabel = archived
    ? `Restore project ${project.name}`
    : `Archive project ${project.name}`;

  return (
    <DataRow
      to={`/projects/${encodeURIComponent(project.id)}`}
      title={project.name}
      {...(copiedFrom ? { subtitle: copiedFrom } : {})}
      className={`grid ${COLUMNS} items-center gap-3 px-3.5 py-3`}
      columns={
        // `display: contents` promotes these two cells into the row's own grid,
        // so one copy of each cell serves every width. A responsive variant
        // would mean two copies in the DOM and two announcements for one row.
        <div className="contents">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="neutral">{readinessLabel(project.readiness)}</Badge>
            {publicationBadge ? (
              <Badge tone="outline">{publicationBadge}</Badge>
            ) : null}
            {/*
              In the badge cluster, not a fifth column: the four-track grid is
              the canonical reference's, and `AssetStateBadges` set the
              precedent that archived state rides beside the other flags.
            */}
            {archived ? <Badge tone="outline">Archived</Badge> : null}
            {picked ? <Badge tone="success">Picked</Badge> : null}
          </div>
          <span className="truncate text-sm text-muted-foreground">
            {/*
              The id, verbatim. `/v1/preview-targets` returns bare ids and
              `ProjectListItem` carries no display name, so there is no
              organisation name to render and none is invented here — ratified
              tenant-neutrality keeps real customer names out of active-surface
              code. The canonical `Preview target required` state is likewise
              absent: `previewTenantId` is required with `minLength: 1`, so the
              contract cannot express a Project without one.
            */}
            {project.previewTenantId}
          </span>
        </div>
      }
      actions={
        <>
          {/*
            Pick for production (ADR 0008 addendum): a pointer the Editor keeps
            per tenant, gated by `signoff.create` because choosing what is
            intended for production is an acceptance decision. Disabled with the
            reason, never absent. It sets the pick only — it publishes nothing.
          */}
          <Tooltip
            content={pickRefusal ?? `Pick ${project.name}`}
          >
            <IconButton
              label={`Pick project ${project.name}`}
              showTooltip={false}
              disabled={pickRefusal !== null}
              icon={<Star className="h-4 w-4" />}
              onClick={() => {
                if (pickRefusal === null) {
                  onPick(project);
                }
              }}
            />
          </Tooltip>
          {/* Read-only, so archived projects keep it. */}
          <Tooltip content="Open Reliv Labs">
            <Button asChild variant="outline" size="icon">
              <Link
                to={labPath(project.id)}
                aria-label={`Open Reliv Labs for ${project.name}`}
              >
                <FlaskConical aria-hidden="true" className="h-4 w-4" />
              </Link>
            </Button>
          </Tooltip>
          {archived && !restoreAllowed ? null : (
            // Painted above the row's stretched navigation link by `DataRow`, so
            // the browser hit-tests the control and never the link underneath it.
            // The tooltip carries the refusal when there is one and the action's
            // own name when there is not, `LibraryCatalog`'s shape.
            <Tooltip content={refusal ?? actionLabel}>
              <IconButton
                label={actionLabel}
                showTooltip={false}
                disabled={refusal !== null}
                icon={
                  archived ? (
                    <ArchiveRestore className="h-4 w-4" />
                  ) : (
                    <Archive className="h-4 w-4" />
                  )
                }
                onClick={() => {
                  if (refusal === null) {
                    onLifecycle(project, mode);
                  }
                }}
              />
            </Tooltip>
          )}
          {/* Read-only, so archived projects keep it. */}
          <Tooltip content="Project settings">
            <IconButton
              label={`Project settings for ${project.name}`}
              showTooltip={false}
              icon={<Info className="h-4 w-4" />}
              onClick={() => onSettings(project)}
            />
          </Tooltip>
        </>
      }
    />
  );
}

/**
 * The landing view: every Project, its status, and what its previews run
 * against.
 *
 * Converged on the canonical reference by #439's 2026-08-25 amendment, which
 * replaced this route's scope outright. What left with it was the actionable
 * queue the ticket originally described — the `Needs attention` adornment, the
 * base-pack digest column and the `basedOn` subtitle — and the amendment is
 * explicit that the readiness and source fields behind them stay on the API,
 * unremoved, with no user-facing surface here.
 *
 * Every authorized Mentaily user sees this same list: #202 PR B2 collapsed
 * project scoping onto the deployment's environment, so there is no per-user
 * slice of it any more.
 */
export function ProjectsIndexRoute() {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [lifecycle, setLifecycle] = useState<{
    project: ProjectListItem;
    mode: ProjectLifecycleMode;
  } | null>(null);
  const [settings, setSettings] = useState<ProjectListItem | null>(null);
  // The tenant is a GROUPING ATTRIBUTE of a Project's preview target, never an
  // owner (ADR 0008 addendum): the filter narrows the list and grants nothing
  // the unfiltered list does not, and grouping is presentation.
  const [tenantFilter, setTenantFilter] = useState("");
  const [groupByTenant, setGroupByTenant] = useState(false);
  const [pickFailure, setPickFailure] = useState<TenantPickFailure | null>(
    null,
  );
  const searchInputId = useId();
  const tenantFilterId = useId();
  const tabsId = useId();
  // #1165: the Tenants tab, kept in the address so its pages can link back.
  const [params, setParams] = useSearchParams();
  const { tenantTools } = useRuntimeConfig().features;
  const views = tenantTools
    ? VIEWS
    : VIEWS.filter(({ id }) => id !== "environments");
  const view: ViewId =
    views.find(({ id }) => id === params.get("view"))?.id ?? "projects";
  const projects = useProjectsQuery(search, includeArchived, tenantFilter);
  const targets = usePreviewTargetsQuery();
  const picks = useTenantPicksQuery();
  const putPick = usePutTenantPickMutation();
  const toast = useToast();
  const createProject = useCapability("project.create");
  const archiveProject = useCapability("project.archive");
  const restoreProject = useCapability("project.restore");
  const pickProject = useCapability("signoff.create");

  const pickByTenant = new Map<string, TenantProjectPick>(
    (picks.data?.items ?? []).map((pick) => [pick.previewTenantId, pick]),
  );
  // The tenants a clinician can filter to: the available targets, the tenants
  // that already hold a pick, and the tenants of the Projects on screen.
  const tenantOptions = [
    ...new Set(
      [
        ...(targets.data?.items ?? []),
        ...pickByTenant.keys(),
        ...(projects.data?.items ?? []).map((item) => item.previewTenantId),
        tenantFilter,
      ].filter(
        (tenant): tenant is string =>
          typeof tenant === "string" && tenant !== "",
      ),
    ),
  ].sort();

  function pickRefusalFor(project: ProjectListItem): string | null {
    if (!pickProject.allowed) {
      return pickProject.reason;
    }
    if (project.archive?.archived === true) {
      return "Archived projects cannot be picked.";
    }
    if (pickByTenant.get(project.previewTenantId)?.projectId === project.id) {
      return "Already picked.";
    }
    return null;
  }

  function pick(project: ProjectListItem) {
    setPickFailure(null);
    const current = pickByTenant.get(project.previewTenantId);
    putPick.mutate(
      {
        previewTenantId: project.previewTenantId,
        projectId: project.id,
        ...(current ? { expectedRevision: current.revision } : {}),
      },
      {
        onSuccess: () => toast(`${project.name} is picked.`),
        onError: (error) => setPickFailure(classifyTenantPickFailure(error)),
      },
    );
  }

  function showView(next: ViewId) {
    setParams(next === "projects" ? {} : { view: next }, { replace: true });
  }

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const step = event.key === "ArrowRight" ? 1 : views.length - 1;
    const index = views.findIndex(({ id }) => id === view);
    const next = views[(index + step) % views.length]?.id ?? view;
    showView(next);
    document.getElementById(`${tabsId}-${next}`)?.focus();
  }

  const rowFor = (project: ProjectListItem) => (
    <ProjectRow
      key={project.id}
      project={project}
      archiveRefusal={archiveProject.allowed ? null : archiveProject.reason}
      restoreAllowed={restoreProject.allowed}
      onLifecycle={(target, mode) => setLifecycle({ project: target, mode })}
      onSettings={setSettings}
      picked={
        pickByTenant.get(project.previewTenantId)?.projectId === project.id
      }
      pickRefusal={pickRefusalFor(project)}
      onPick={pick}
    />
  );
  // Tenants in name order, each with its Projects in the list's own order.
  const groups = groupByTenant
    ? groupByPreviewTenant(projects.data?.items ?? [])
    : [];

  return (
    <>
      <PageHeader
        title="Projects"
        // Canonical, verbatim. This read "the base pack it pins" while the
        // route rendered a digest column; #202 PR B2's boundary — that what a
        // Project RUNS is a deployment fact this service is not the authority
        // for — governs what may be PROJECTED as a field, not the wording of a
        // page description, and #439 restores the canonical sentence.
        description="The landing view. All projects, what they run, what needs attention."
        actions={
          // #567: gated on the capability the CREATE itself requires, so a
          // subject without `project.create` never reaches a dialog whose
          // submit the server would refuse anyway. Disabled-with-reason, not
          // hidden — the server still enforces; this mirrors its decision.
          <Button
            disabled={!createProject.allowed}
            title={createProject.reason ?? undefined}
            onClick={() => {
              if (createProject.allowed) setCreating(true);
            }}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            New project
          </Button>
        }
      />

      {/* Mounted only while open, so each creation starts from a clean form. */}
      {creating ? (
        <NewProjectDialog onClose={() => setCreating(false)} />
      ) : null}

      {/* Mounted only while open, so each decision reads a fresh revision. */}
      {lifecycle ? (
        <ProjectLifecycleDialog
          project={lifecycle.project}
          mode={lifecycle.mode}
          onClose={() => setLifecycle(null)}
        />
      ) : null}

      {settings ? (
        <ProjectSettingsDialog
          projectId={settings.id}
          lineage={settings.lineage}
          lastEdited={settings.lastMeaningfulActivityAt}
          onClose={() => setSettings(null)}
        />
      ) : null}

      <div
        role="tablist"
        aria-label="View"
        className="mb-4 flex border-b border-border"
      >
        {views.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`${tabsId}-${id}`}
            aria-selected={view === id}
            // Only the shown panel is in the document to point at.
            aria-controls={view === id ? `${tabsId}-${id}-panel` : undefined}
            tabIndex={view === id ? 0 : -1}
            className={cn(
              "-mb-px border-b-2 px-3.5 py-2 text-sm",
              view === id
                ? "border-primary font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            onClick={() => showView(id)}
            onKeyDown={onTabKeyDown}
          >
            {label}{" "}
            <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground tabular-nums">
              {id === "projects"
                ? (projects.data?.items.length ?? "")
                : id === "tenants"
                  ? (targets.data?.items.length ?? "")
                  : ""}
            </span>
          </button>
        ))}
      </div>

      {view === "tenants" ? (
        <div
          role="tabpanel"
          id={`${tabsId}-tenants-panel`}
          aria-labelledby={`${tabsId}-tenants`}
        >
          <TenantsPanel />
        </div>
      ) : null}

      {view === "environments" ? (
        <div
          role="tabpanel"
          id={`${tabsId}-environments-panel`}
          aria-labelledby={`${tabsId}-environments`}
        >
          <EnvironmentsPanel />
        </div>
      ) : null}

      <div
        className="space-y-4"
        role="tabpanel"
        id={`${tabsId}-projects-panel`}
        aria-labelledby={`${tabsId}-projects`}
        hidden={view !== "projects"}
      >
        <label htmlFor={searchInputId} className="sr-only">
          Search projects
        </label>
        <div className="flex items-center gap-2">
          <Input
            id={searchInputId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search projects"
            leadingIcon={<Search className="h-4 w-4" />}
            className="flex-1"
          />
          {/*
            `LibraryCatalog`'s toggle, for the same reason it exists there:
            archived Projects are hidden by default so the index cannot offer
            them (#434's listing rule), and looking at them is an explicit
            act — soft state, not secrecy. `aria-pressed` because it is a
            toggle, not a navigation.
          */}
          <Button
            variant="outline"
            aria-pressed={includeArchived}
            onClick={() => setIncludeArchived((current) => !current)}
          >
            Show archived
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={tenantFilterId} className="sr-only">
            Filter by preview target
          </label>
          <Select
            id={tenantFilterId}
            value={tenantFilter}
            onChange={(event) => setTenantFilter(event.target.value)}
            className="max-w-xs"
          >
            <option value="">All preview targets</option>
            {tenantOptions.map((tenant) => (
              <option key={tenant} value={tenant}>
                {tenant}
              </option>
            ))}
          </Select>
          <Button
            variant="outline"
            aria-pressed={groupByTenant}
            onClick={() => setGroupByTenant((current) => !current)}
          >
            Group by preview target
          </Button>
        </div>

        {pickFailure !== null ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {PICK_FAILURE_MESSAGES[pickFailure.kind]}
          </p>
        ) : null}

        {/*
          A transient failed *refresh* over a list already in the cache is not
          the same failure as a failed first load. Discarding the list costs the
          clinician the page; showing it without saying so lets them act on data
          that is older than it looks. The live region stays mounted so
          assistive technology observes the notice when it becomes visible.
        */}
        {projects.data ? (
          <StaleDataNotice
            updatedAt={projects.dataUpdatedAt}
            onRetry={() => void projects.refetch()}
            visible={projects.isError && !isTerminalQueryError(projects.error)}
          />
        ) : null}

        {projects.isPending ? (
          <QueryPendingState
            isPaused={projects.isPaused}
            failureReason={projects.failureReason}
            label="Loading projects"
            errorTitle="Projects could not be loaded"
            rows={4}
            queryKey={queryKeys.projects(search, includeArchived, tenantFilter)}
          />
        ) : projects.isError &&
          (!projects.data || isTerminalQueryError(projects.error)) ? (
          <QueryErrorState
            error={projects.error}
            subject="Projects"
            title="Projects could not be loaded"
            description="The Editor Service did not return the project list."
            deniedDescription="You are not authorized to list projects in this environment."
            onRetry={() => void projects.refetch()}
          />
        ) : projects.data.items.length === 0 && !search && !tenantFilter ? (
          <EmptyState
            title={includeArchived ? "No projects yet" : "No active projects"}
            description={
              includeArchived
                ? "Projects you are authorized to open will appear here."
                : "Active projects you are authorized to open will appear here. Use Show archived to view retained projects."
            }
          />
        ) : groupByTenant && projects.data.items.length > 0 ? (
          <div className="space-y-6">
            {groups.map(([tenant, items]) => {
              const picked = pickByTenant.get(tenant);
              const pickedName = items.find(
                (item) => item.id === picked?.projectId,
              )?.name;
              return (
                <section key={tenant} aria-label={`Projects on ${tenant}`}>
                  <h2 className="mb-2 flex flex-wrap items-baseline gap-2 text-sm font-semibold text-foreground">
                    <span dir="auto">{tenant}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {items.length}{" "}
                      {items.length === 1 ? "project" : "projects"}
                      {pickedName ? " · picked: " : ""}
                      {pickedName ? <span dir="auto">{pickedName}</span> : null}
                    </span>
                  </h2>
                  <Card className="overflow-hidden">
                    <ColumnHeader />
                    {items.map(rowFor)}
                  </Card>
                </section>
              );
            })}
          </div>
        ) : (
          <Card className="overflow-hidden">
            <ColumnHeader />
            {projects.data.items.length === 0 ? (
              /*
                NO MATCH, and the table stays. The canonical surface keeps the
                column header above this sentence rather than replacing the
                whole table with a panel, because the search that produced it is
                still on screen and the columns are what the next keystroke will
                fill. An empty DATABASE is a different answer and keeps its own
                panel above.
              */
              <div className="px-3.5 py-8 text-center text-sm text-muted-foreground">
                No projects match your search.
              </div>
            ) : (
              projects.data.items.map(rowFor)
            )}
          </Card>
        )}
      </div>
    </>
  );
}
